import React from 'react';
import NProgress from 'nprogress';
import Router from 'next/router';
import Head from 'next/head';
import localFont from 'next/font/local';
import { AppProps } from 'next/app';
import { ToastContainer, toast } from 'react-toastify';
import '../styles.css';
import 'react-toastify/dist/ReactToastify.css';

import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { ApplicationContextProvider, UserContextProvider } from '../context';

const myFont = localFont({
  src: [
    {
      path: '../public/fonts/Aeroport-Light.otf',
      style: 'normal',
      weight: '300',
    },
    {
      path: '../public/fonts/Aeroport-Light-Italic.otf',
      style: 'italic',
      weight: '300',
    },
    {
      path: '../public/fonts/Aeroport.otf',
      style: 'normal',
      weight: '400',
    },
    {
      path: '../public/fonts/Aeroport-Italic.otf',
      style: 'italic',
      weight: '400',
    },
    {
      path: '../public/fonts/Aeroport-Medium.otf',
      style: 'normal',
      weight: '600',
    },
    {
      path: '../public/fonts/Aeroport-Medium-Italic.otf',
      style: 'italic',
      weight: '600',
    },
    {
      path: '../public/fonts/Aeroport-Bold.otf',
      style: 'normal',
      weight: '700',
    },
    {
      path: '../public/fonts/Aeroport-Bold-Italic.otf',
      style: 'italic',
      weight: '700',
    },
  ],
  variable: '--font-Aeroport',
});

function MyApp({ Component, pageProps }: AppProps) {
  // Initialize PostHog and capture pageviews
  const oldUrlRef = React.useRef('');

  React.useEffect(() => {
    posthog?.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
      api_host: '/ingest',
      ui_host: 'https://us.posthog?.com',
      capture_exceptions: true,
      loaded: (ph) => {
        if (process.env.NODE_ENV === 'development') ph.debug();
      },
      debug: process.env.NODE_ENV === 'development',
    });

    const handleRouteChange = () => posthog?.capture('$pageview');

    const handleRouteChangeStart = () =>
      posthog?.capture('$pageleave', {
        $current_url: oldUrlRef.current,
      });

    Router.events.on('routeChangeComplete', handleRouteChange);
    Router.events.on('routeChangeStart', handleRouteChangeStart);

    return () => {
      Router.events.off('routeChangeComplete', handleRouteChange);
      Router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, []);

  // NProgress loading indicator
  React.useEffect(() => {
    Router.events.on('routeChangeStart', () => {
      NProgress.start();
    });

    Router.events.on('routeChangeComplete', () => {
      NProgress.done();
    });

    Router.events.on('routeChangeError', () => {
      NProgress.done();
    });
  }, []);

  // Network status toasts
  React.useEffect(function networkEventListener() {
    if (typeof window !== 'undefined') {
      window.addEventListener(
        'offline',
        () => {
          toast.error(
            'You are offline, please check your internet connection.',
            {
              position: 'top-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            }
          );
        },
        false
      );
      window.addEventListener(
        'online',
        () => {
          toast.success('You are back online.', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
        },
        false
      );
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('offline', () => {}, false);
        window.removeEventListener('online', () => {}, false);
      }
    };
  }, []);

  return (
    <PostHogProvider client={posthog}>
      <>
        <Head>
          <title>Carreras | A better tomorrow</title>
        </Head>
        <main className={myFont.className}>
          <UserContextProvider>
            <ApplicationContextProvider>
              <Component {...pageProps} />
            </ApplicationContextProvider>
          </UserContextProvider>
        </main>
        <ToastContainer />
      </>
    </PostHogProvider>
  );
}

export default MyApp;
